import * as React from 'react';

//Front
import { ReportTable } from './ReportTable';
import type { ReportType, ReportCriterion } from '../../../Constants/AgencyReports';
import { FILTERABLE_FIELDS_BY_REPORT, REPORT_HEADERS } from '../../../Constants/AgencyReports';

//API
import type { ReportFilterOption } from '../../../../MedicareProAPI/Entities/Reports/Agency/ReportFilterOption';
import type { ReportFilterOptionCollection } from '../../../../MedicareProAPI/Collections/Reports/Agency/ReportFilterOptionCollection';
import type { Report } from '../../../../MedicareProAPI/Entities/Reports/Report';
import type { ReportCollection } from '../../../../MedicareProAPI/Collections/Reports/ReportCollection';
import { AgencyReportRepository } from '../../../../MedicareProAPI/Repositories/Reports/AgencyReportRepository';
import { ReportBuilder } from './ReportBuilder';
import { AgencySettingRepository } from '../../../../MedicareProAPI/Repositories/Agency/AgencySettingRepository';

export interface ReportViewerProps {
  userId: number;
  userAccessToken: string;
  agencyId: number;
  selectedReportType: ReportType;
  contractsInherited: boolean;
  absoluteUplineId?: number;
}

export interface FilterOption {
  label: string;
  value: string;
}

/**
 * Parent component that wraps the report builder and table and performs API calls.
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-06-16
 * @lastUpdated
 * @version 1.1.0
 */

export const ReportViewer = (props: ReportViewerProps): React.JSX.Element => {
  const agencyReportRepository = new AgencyReportRepository();
  agencyReportRepository.setAccessToken(props.userAccessToken);

  const agencySettingRepository = new AgencySettingRepository();
  agencySettingRepository.setAccessToken(props.userAccessToken);

  const [filterSelected, setFilterSelected] = React.useState<FilterOption | null>(null);
  const [isFetchingFilterOptions, setIsFetchingFilterOptions] = React.useState<boolean>(false);
  const [isFetchingResults, setIsFetchingResults] = React.useState<boolean>(false);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const [filterOptions, setFilterOptions] = React.useState<Array<ReportFilterOption>>([]);
  const [selectedFilters, setSelectedFilters] = React.useState<string[] | number[]>([]);
  const [reportResults, setReportResults] = React.useState<Array<Report>>([]);
  const [criteria, setCriteria] = React.useState<ReportCriterion[]>([]);
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [thisContractsInherited, setThisContractsInherited] = React.useState(
    props.contractsInherited
  );

  const headers = REPORT_HEADERS[props.selectedReportType.id];

  /**
   * Runs a report by querying the API with filters and pagination,
   * then stores the results in component state.
   *
   * @param userId - ID of the user requesting the report
   * @param agencyId - ID of the agency associated with the report
   * @param reportName - Key or slug of the report to run
   * @param criteria - Filter values used to query the report
   * @param offsetOverride - Pagination offset (default: 0)
   * @param limitOverride - Pagination limit (default: current `limit` state)
   * @returns void
   */
  const handleRunReport = (
    userId: number,
    agencyId: number,
    reportName: string,
    criteria: any,
    offsetOverride: number = 0,
    limitOverride: number = limit
  ): void => {
    setCriteria(criteria);
    setIsFetchingResults(true);
    setReportResults([]);
    setTotal(0);

    agencyReportRepository
      .getReport(userId, agencyId, reportName, {
        criteria,
        offset: offsetOverride,
        limit: limitOverride,
      })
      .then((salesByCustom: ReportCollection) => {
        setReportResults(salesByCustom.getItems());
        setOffset(salesByCustom.getOffset());
        setLimit(salesByCustom.getLimit());
        setTotal(salesByCustom.getTotal());
      })
      // Catch errors
      .catch((e) => {})
      // Finally set isFetchingResults to false
      .finally(() => {
        setIsFetchingResults(false);
      });
  };

  /**
   * Initiates a CSV export for the selected report with applied filters.
   * Sets exporting state, builds criteria, and triggers the export via the repository.
   *
   * @param userId - ID of the user requesting the export
   * @param agencyId - ID of the agency associated with the export
   * @param reportName - Name of the report to export
   * @param selectedFilters - Selected filter values (typically plan names or types)
   * @param dateFrom - Start date for filtering results
   * @param dateTo - End date for filtering results
   */
  const handleExport = (userId: number, agencyId: number, reportName: string, criteria: any) => {
    setIsExporting(true);

    agencyReportRepository
      .getReportExport(userId, agencyId, reportName, { criteria: criteria })
      .then((response) => {
        setIsExporting(false);
      });
  };

  /**
   * Toggle whether an agency inherits contracts from its absolute upline agency.
   *
   * Side effects:
   * - Optimistically updates local UI state via `setThisContractsInherited`.
   * - Fires a repository update to persist the flag on the server.
   *
   * @param inherit  When true, mark contracts as inherited; when false, use local contracts.
   * @param agencyId The agency whose setting is being updated.
   */
  const handleInheritContracts = (inherit: boolean, agencyId: number): void => {
    setThisContractsInherited(inherit);
    setReportResults([]);
    agencySettingRepository.updateAgencySetting(agencyId, { inheritsContractsfrom: inherit });
  };

  React.useEffect(() => {
    if (!filterSelected) return;

    setIsFetchingFilterOptions(true);

    if (filterSelected.value.indexOf('_all') < 0) {
      agencyReportRepository
        .getReportFilterOptions(props.userId, props.agencyId, filterSelected.value)
        .then((reportFilterOptions: ReportFilterOptionCollection) => {
          setFilterOptions(reportFilterOptions.getItems());
        })
        .catch((error) => {
          console.error('Failed to fetch filter options:', error);
          setFilterOptions([]);
        })
        .finally(() => {
          setIsFetchingFilterOptions(false);
        });
    }
  }, [filterSelected, props.userId, props.agencyId]);

  React.useEffect(() => {
    setIsFetchingResults(false);
    setReportResults([]);
    setLimit(25);
    setTotal(0);
    setOffset(0);
  }, [props.selectedReportType, filterSelected]);

  return (
    <div className="card" style={{ height: 'auto' }}>
      <div className="card-header font-weight-bold py-2 cp">
        {props.selectedReportType.title} <span className="ml-2 reportSpin"></span>
      </div>

      <div className="card-body pt-2">
        {/* Report Builder*/}

        <ReportBuilder
          reportType={props.selectedReportType}
          onReportFilterSelect={setFilterSelected}
          onRunReport={handleRunReport}
          onExport={handleExport}
          onInheritChange={handleInheritContracts}
          isFetchingResults={isFetchingResults}
          isExporting={isExporting}
          userId={props.userId}
          agencyId={props.agencyId}
          userAccessToken={props.userAccessToken}
          contractsInherited={thisContractsInherited}
          absoluteUplineId={props.absoluteUplineId}
        />

        {/* Report results */}
        <ReportTable
          headerValues={headers}
          rows={reportResults}
          isLoading={isFetchingResults}
          limit={limit}
          offset={offset}
          total={total}
          onPageChange={(offset) => {
            handleRunReport(
              props.userId,
              props.agencyId,
              filterSelected.value,
              criteria,
              offset,
              limit
            );
          }}
        />
      </div>
    </div>
  );
};
